import { Key, memo, MouseEventHandler, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';
import { LoadingRow } from '../Table/components';
import { Alignment } from '../Table/types';

export type ColumnDef<T> = {
  id: Key;
  name: string;
  accessor?: keyof T;
  render?: () => ReactNode;
  renderRow?: (row: T) => ReactNode;
  alignment?: Alignment;
  rowContainerClassNames?: string;
  tdClassNames?: string;
};

export type RowDef<T> = { id: Key; onClick?: MouseEventHandler } & T;

type RowProps<T> = {
  row: RowDef<T>;
  columns: ColumnDef<T>[];
};

const Row = <T,>({ row, columns }: RowProps<T>) => {
  return (
    <tr
      onClick={row.onClick}
      className={twMerge(!!row.onClick && 'cursor-pointer')}
    >
      {columns.map((column) => (
        <td
          key={`${column.id}_${row.id}`}
          align={column.alignment ?? 'center'}
          className={twMerge(
            'group m-0 border-collapse border-spacing-0 border-b-4 border-transparent p-0',
            column.tdClassNames,
          )}
        >
          <div
            className={twMerge(
              'flex h-11.25 items-center justify-center overflow-hidden overflow-ellipsis bg-white px-2.5 text-sm text-primaryBlue',
              'border-b-1 border-t-1 border-rinseGray group-first:rounded-l-md group-first:border-l group-last:rounded-r-md group-last:border-r group-last:border-t',
              column.alignment === 'left' && 'justify-start',
              column.alignment === 'right' && 'justify-end',
              column.rowContainerClassNames,
            )}
          >
            {column.renderRow?.(row) ??
              (column.accessor && (
                <span className="w-full overflow-hidden text-ellipsis text-nowrap">
                  {`${row[column.accessor]}`}
                </span>
              ))}
          </div>
        </td>
      ))}
    </tr>
  );
};

export type TableV2Props<T> = {
  rows: RowDef<T>[];
  columns: ColumnDef<T>[];
  emptyStateMessage: ReactNode;
  isLoading?: boolean;
  loadingRows?: number;
};

const TableComponent = <T,>({
  columns,
  rows,
  emptyStateMessage,
  isLoading,
  loadingRows,
}: TableV2Props<T>) => {
  return (
    <div className="w-full overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                align={column.alignment ?? 'center'}
                className={twMerge(
                  'h-8 border-b-2.5 border-transparent bg-inherit px-2.5 text-xs font-black leading-xs text-spartanBlue',
                  column.tdClassNames,
                )}
              >
                {column.render?.() ?? column.name}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {!isLoading && !rows.length && !!emptyStateMessage && (
            <tr className="h-16">
              <td colSpan={columns.length} align="center" className="text-md">
                {emptyStateMessage}
              </td>
            </tr>
          )}
          {isLoading
            ? Array.from({ length: loadingRows ?? 0 }).map((_, rowIndex) => (
                <LoadingRow
                  key={`loading-${rowIndex}`}
                  rowIndex={rowIndex}
                  columnsCount={columns.length}
                />
              ))
            : rows.map((row) => (
                <Row key={row.id} row={row} columns={columns} />
              ))}
        </tbody>
      </table>
    </div>
  );
};

export const TableV2 = memo(TableComponent) as typeof TableComponent;
